import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, ClickAwayListener, ListItemText, Popper, Typography, useTheme, } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { Fragment, useEffect, useMemo, useRef, useState, } from 'react';
import { groupBy } from 'remeda';
import { IconContainer, MenuDivider, MenuList, MenuListItemButton, MenuListItemIcon, MenuListSubheader, MenuPaper, PrefixLabelBox, } from './menu.styled';
const createVirtualElement = (rect) => {
    const cloned = new DOMRect(rect.x, rect.y, rect.width, rect.height);
    return {
        getBoundingClientRect: () => cloned,
        contextElement: typeof document === 'undefined' ? null : document.body,
    };
};
const groupItems = (items) => {
    const itemsWithIndex = items.map((item, index) => ({ item, index }));
    const grouped = groupBy(itemsWithIndex, (entry) => entry.item.group ?? '');
    return Object.entries(grouped).map(([groupKey, entries]) => ({
        group: groupKey === '' ? undefined : groupKey,
        entries,
    }));
};
const MENU_TEXT_FONT_SIZE = '0.6rem';
// Popper only accepts DOM nodes, so wrap the raw DOMRect in a “virtual” element that
// mimics getBoundingClientRect, letting us anchor the menu to the caret position.
const EMPTY_VIRTUAL_ELEMENT = {
    getBoundingClientRect: () => new DOMRect(0, 0, 0, 0),
};
export const SlashCommandMenu = ({ open, anchorEl, items, selectedIndex, onSelect, onHighlight, onClose, theme, }) => {
    const themeFromContext = useTheme();
    const resolvedTheme = theme ?? themeFromContext;
    const [virtualElement, setVirtualElement] = useState(null);
    const selectedItemRef = useRef(null);
    useEffect(() => {
        if (!anchorEl) {
            setVirtualElement(null);
            return;
        }
        setVirtualElement(createVirtualElement(anchorEl));
    }, [
        anchorEl?.x,
        anchorEl?.y,
        anchorEl?.top,
        anchorEl?.left,
        anchorEl?.width,
        anchorEl?.height,
    ]);
    const groupedItems = useMemo(() => groupItems(items), [items]);
    const handleMouseDown = (event) => {
        event.preventDefault();
    };
    const handleSelect = (index) => (event) => {
        event.preventDefault();
        onSelect(index);
    };
    const handleHighlight = (index) => () => {
        onHighlight?.(index);
    };
    const referenceEl = virtualElement ?? (anchorEl ? createVirtualElement(anchorEl) : null);
    useEffect(() => {
        if (!open) {
            selectedItemRef.current = null;
            return;
        }
        const element = selectedItemRef.current;
        if (!element) {
            return;
        }
        // Use requestAnimationFrame to wait for layout before scrolling.
        const frame = window.requestAnimationFrame(() => {
            element.scrollIntoView({ block: 'nearest' });
        });
        return () => {
            window.cancelAnimationFrame(frame);
        };
    }, [open, selectedIndex, items]);
    const handleClickAway = (event) => {
        if (!open || !onClose) {
            return;
        }
        const target = event.target;
        if (target?.closest('.portal-editor, .ProseMirror')) {
            return;
        }
        onClose();
    };
    const menuContent = (_jsx(Popper, { open: open, placement: "bottom-start", anchorEl: referenceEl ?? EMPTY_VIRTUAL_ELEMENT, modifiers: [
            { name: 'offset', options: { offset: [0, 4] } },
            { name: 'preventOverflow', options: { mainAxis: true, altAxis: true } },
        ], style: { zIndex: resolvedTheme.zIndex.modal }, children: _jsx(ClickAwayListener, { onClickAway: handleClickAway, children: _jsx(MenuPaper, { elevation: 8, onMouseDown: handleMouseDown, children: items.length === 0 ? (_jsx(Box, { paddingX: 2, paddingY: 1.5, display: "flex", alignItems: "center", justifyContent: "center", children: _jsx(Typography, { variant: "body2", color: resolvedTheme.palette.text.secondary, "data-testid": "slash-command-menu-empty", children: "No matching commands" }) })) : (_jsx(MenuList, { disablePadding: true, dense: true, children: groupedItems.map((group, groupIdx) => (_jsxs(Fragment, { children: [group.group ? (_jsx(MenuListSubheader, { disableSticky: true, children: group.group })) : null, group.entries.map(({ item, index }) => {
                                const isSelected = index === selectedIndex;
                                const IconComponent = item.icon;
                                const prefixLabel = item.prefixLabel;
                                return (_jsxs(MenuListItemButton, { selected: isSelected, onClick: handleSelect(index), onMouseEnter: handleHighlight(index), ref: isSelected
                                        ? (node) => {
                                            selectedItemRef.current = node;
                                        }
                                        : undefined, children: [IconComponent || prefixLabel ? (_jsx(MenuListItemIcon, { isSelected: isSelected, children: _jsx(IconContainer, { children: IconComponent ? (_jsx(IconComponent, { fontSize: "small", sx: {
                                                        fontSize: '0.8rem',
                                                    } })) : (_jsx(PrefixLabelBox, { component: "span", children: prefixLabel })) }) })) : null, _jsx(ListItemText, { primary: item.title, secondary: item.description, slotProps: {
                                                primary: {
                                                    variant: 'body2',
                                                    fontWeight: isSelected ? 600 : 500,
                                                    style: { fontSize: MENU_TEXT_FONT_SIZE },
                                                },
                                                secondary: {
                                                    variant: 'caption',
                                                    color: resolvedTheme.palette.text.secondary,
                                                    style: { fontSize: MENU_TEXT_FONT_SIZE, lineHeight: '1' },
                                                },
                                            } })] }, item.id));
                            }), groupIdx < groupedItems.length - 1 ? _jsx(MenuDivider, { component: "li" }) : null] }, group.group ?? `group-${groupIdx}`))) })) }) }) }));
    return theme ? (_jsx(ThemeProvider, { theme: resolvedTheme, children: menuContent })) : (menuContent);
};
